import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  CreateNewFolder as CreateFolderIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Folder as FolderIcon,
  FolderOpen as FolderOpenIcon,
  Home as HomeIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
  NavigateNext as NavigateNextIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  ShowChart as EcgIcon,
  Upload as UploadIcon,
  Videocam as VideoIcon,
  Visibility as ViewIcon,
  Accessibility as VpfIcon,
} from '@mui/icons-material';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/PageHeader';
import NotificationDialog from '../components/NotificationDialog';
import {
  browseLibrary,
  defaultFileTag,
  FILE_TYPE_LABELS,
  FILE_TYPE_VALUES,
  FILE_TYPES,
  folderBreadcrumbs,
  folderPath,
  formatFileSize,
  inferFileType,
  MAX_FILE_BYTES,
  myFilesFolderPath,
  myFilesHomePath,
  resolveLibraryFolderId,
  validateUploads,
} from '../utils/myFiles';
import {
  createMyFolder,
  deleteMyFile,
  deleteMyFolder,
  downloadMyFile,
  listMyFiles,
  replaceMyFile,
  saveMyFile,
  saveMyFolder,
  uploadMyFile,
} from '../utils/userContentApi';

function TypeIcon({ fileType }) {
  const props = { color: 'action' };
  if (fileType === FILE_TYPES.IMAGE) return <ImageIcon {...props} />;
  if (fileType === FILE_TYPES.VIDEO) return <VideoIcon {...props} />;
  if (fileType === FILE_TYPES.ECG) return <EcgIcon {...props} />;
  if (fileType === FILE_TYPES.CASE_LIBRARY_VPF || fileType === FILE_TYPES.CUSTOM_VPF) {
    return <VpfIcon {...props} />;
  }
  return <FileIcon {...props} />;
}

function uploadItemsFrom(fileList) {
  return Array.from(fileList || []).map((file) => ({
    key: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
    file,
    originalName: file.name,
    fileTag: defaultFileTag(file.name),
    fileType: inferFileType(file.name),
    customVpfSourcePath: '',
    fileSize: file.size || 0,
  }));
}

const MyFilesBrowser = () => {
  const navigate = useNavigate();
  const { folderId: folderIdParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const decodedFolderParam = folderIdParam ? decodeURIComponent(folderIdParam) : '';
  const [library, setLibrary] = useState({ folders: [], files: [] });
  const [ready, setReady] = useState(false);
  const [query, setQuery] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadStep, setUploadStep] = useState('select');
  const [uploadItems, setUploadItems] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [folderDialog, setFolderDialog] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const detailsId = searchParams.get('file') || '';
  const setDetailsId = useCallback((id) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set('file', id);
      else next.delete('file');
      return next;
    });
  }, [setSearchParams]);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [menu, setMenu] = useState(null);
  const [notice, setNotice] = useState(null);
  const [detailsUrl, setDetailsUrl] = useState('');
  const libraryRef = useRef(library);
  libraryRef.current = library;

  const refreshLibrary = () => listMyFiles().then((next) => {
    const libraryNext = next && typeof next === 'object' ? next : { folders: [], files: [] };
    libraryRef.current = libraryNext;
    setLibrary(libraryNext);
    return libraryNext;
  });

  useEffect(() => {
    let cancelled = false;
    listMyFiles()
      .then((next) => {
        if (cancelled) return;
        const libraryNext = next && typeof next === 'object' ? next : { folders: [], files: [] };
        libraryRef.current = libraryNext;
        setLibrary(libraryNext);
      })
      .catch((error) => {
        if (!cancelled) setNotice({ title: 'Files could not be loaded', message: error.message });
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const resolvedFolderId = useMemo(() => {
    if (!decodedFolderParam) return '';
    return resolveLibraryFolderId(library.folders, decodedFolderParam);
  }, [library.folders, decodedFolderParam]);
  const invalidFolder = Boolean(decodedFolderParam && resolvedFolderId === null);
  const folderId = resolvedFolderId === null ? '' : resolvedFolderId;

  const crumbs = folderBreadcrumbs(library.folders, folderId);
  const items = useMemo(
    () => browseLibrary(library, folderId, query),
    [library, folderId, query]
  );
  const currentName = crumbs[crumbs.length - 1]?.name || 'Home';
  const details = library.files.find((file) => file.id === detailsId) || null;

  useEffect(() => {
    if (!detailsId) {
      setDetailsUrl('');
      return undefined;
    }
    let cancelled = false;
    let url = '';
    downloadMyFile(detailsId)
      .then((blob) => {
        if (cancelled || !(blob instanceof Blob)) return;
        url = URL.createObjectURL(blob);
        setDetailsUrl(url);
      })
      .catch(() => {
        if (!cancelled) setDetailsUrl('');
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [detailsId]);

  useEffect(() => {
    if (!detailsId) return undefined;
    if (!library.files.some((file) => file.id === detailsId)) {
      setDetailsId('');
    }
    return undefined;
  }, [detailsId, library.files, setDetailsId]);

  const openFolder = (id) => {
    navigate(myFilesFolderPath(id));
    setQuery('');
  };

  const closeUpload = () => {
    setUploadOpen(false);
    setUploadStep('select');
    setUploadItems([]);
    setUploadError('');
    setDragOver(false);
  };

  const addUploadFiles = (fileList) => {
    const next = uploadItemsFrom(fileList);
    if (next.length === 0) return;
    setUploadItems((prev) => [...prev, ...next]);
    setUploadError('');
  };

  const submitUpload = () => {
    const error = validateUploads(libraryRef.current, uploadItems);
    if (error) {
      setUploadError(error);
      return;
    }
    Promise.all(uploadItems.map((item) => uploadMyFile({
      file: item.file,
      fileTag: item.fileTag,
      fileType: item.fileType,
      folderId,
      customVpfSourcePath: item.customVpfSourcePath,
      name: item.originalName,
    })))
      .then(() => refreshLibrary())
      .then(() => closeUpload())
      .catch((uploadFailure) => {
        setUploadError(uploadFailure.message || 'Upload failed.');
        refreshLibrary().catch(() => {});
      });
  };

  const submitFolder = () => {
    if (!folderDialog) return;
    const request = folderDialog.mode === 'rename'
      ? saveMyFolder(folderDialog.id, {
          name: folderDialog.name,
          description: folderDialog.description,
          parentId: libraryRef.current.folders.find((folder) => folder.id === folderDialog.id)?.parentId || '',
        })
      : createMyFolder({
          name: folderDialog.name,
          description: folderDialog.description,
          parentId: folderId,
        });
    request
      .then(() => refreshLibrary())
      .then(() => setFolderDialog(null))
      .catch((folderError) => {
        setFolderDialog((prev) => ({ ...prev, error: folderError.message || 'Folder could not be saved.' }));
      });
  };

  const submitRename = () => {
    if (!renameTarget) return;
    const file = libraryRef.current.files.find((item) => item.id === renameTarget.id);
    if (!file) return;
    saveMyFile(file.id, {
      fileTag: renameTarget.tag,
      fileType: file.fileType,
      customVpfSourcePath: file.customVpfSourcePath,
      folderId: file.folderId,
      name: file.name,
    })
      .then(() => refreshLibrary())
      .then(() => setRenameTarget(null))
      .catch((renameError) => {
        setRenameTarget((prev) => ({ ...prev, error: renameError.message || 'The file tag could not be saved.' }));
      });
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    const request = pendingDelete.type === 'folder'
      ? deleteMyFolder(pendingDelete.id)
      : deleteMyFile(pendingDelete.id);
    request
      .then(() => refreshLibrary())
      .then((next) => {
        if (pendingDelete.type === 'folder' && (pendingDelete.id === folderId || !next.folders.some((folder) => folder.id === folderId))) {
          navigate(myFilesHomePath());
        }
        if (detailsId && !next.files.some((file) => file.id === detailsId)) setDetailsId('');
        setPendingDelete(null);
      })
      .catch((deleteError) => {
        setNotice({ title: 'Could not delete', message: deleteError.message || 'Try again.' });
        setPendingDelete(null);
      });
  };

  const downloadFile = (file) => {
    downloadMyFile(file.id)
      .then((blob) => {
        if (!(blob instanceof Blob)) return;
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = file.originalName || file.fileTag;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch((downloadError) => {
        setNotice({ title: 'Could not download', message: downloadError.message || 'Try again.' });
      });
  };

  const replaceDetailsFile = (file) => {
    if (!details || !file) return;
    if (file.size > MAX_FILE_BYTES) {
      setNotice({
        title: 'File is too large',
        message: 'Choose a file that is 15 MB or smaller.',
      });
      return;
    }
    replaceMyFile(details.id, file)
      .then(() => refreshLibrary())
      .then(() => {
        setDetailsUrl('');
        return downloadMyFile(details.id);
      })
      .then((blob) => {
        if (blob instanceof Blob) setDetailsUrl(URL.createObjectURL(blob));
      })
      .catch((replaceError) => {
        setNotice({ title: 'Could not replace file', message: replaceError.message || 'Try again.' });
      });
  };

  const openItem = (item) => {
    if (item.type === 'folder') {
      openFolder(item.id);
      return;
    }
    const file = library.files.find((entry) => entry.id === item.id);
    navigate({
      pathname: myFilesFolderPath(file?.folderId ?? folderId),
      search: `file=${encodeURIComponent(item.id)}`,
    });
  };

  const menuItem = menu?.item;

  if (!ready) {
    return (
      <PageLayout maxWidth="xl">
        <Typography variant="body2" color="text.secondary">Loading files…</Typography>
      </PageLayout>
    );
  }

  if (invalidFolder) {
    return <Navigate to={myFilesHomePath()} replace />;
  }

  return (
    <PageLayout maxWidth="xl">
      <PageHeader
        title="My Files"
        subtitle="Upload files and give each one a file tag. Tags are saved to your account and can be chosen on an imaging row."
      />

      <Paper sx={{ p: 2, mb: 2 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />}>
          {crumbs.map((crumb, index) => (
            <Link
              key={crumb.id || 'home'}
              component={RouterLink}
              to={myFilesFolderPath(crumb.id)}
              variant="body2"
              onClick={() => setQuery('')}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {index === 0 ? <HomeIcon sx={{ mr: 0.5, fontSize: '1rem' }} /> : null}
              {crumb.name}
            </Link>
          ))}
        </Breadcrumbs>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search this folder and subfolders..."
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                endAdornment: query ? (
                  <IconButton aria-label="Clear search" size="small" onClick={() => setQuery('')}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
              <IconButton
                aria-label="Refresh"
                size="small"
                onClick={() => {
                  refreshLibrary().catch((error) => {
                    setNotice({ title: 'Files could not be loaded', message: error.message });
                  });
                }}
              >
                <RefreshIcon />
              </IconButton>
              <Button
                type="button"
                variant="outlined"
                size="small"
                startIcon={<CreateFolderIcon />}
                onClick={() => setFolderDialog({ mode: 'create', name: '', description: '', error: '' })}
              >
                New Folder
              </Button>
              <Button
                type="button"
                variant="contained"
                size="small"
                startIcon={<UploadIcon />}
                onClick={() => setUploadOpen(true)}
              >
                Upload Files
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {query.trim()
              ? `Search results for "${query.trim()}" in ${currentName} (${items.length} found)`
              : `${currentName} (${items.length} ${items.length === 1 ? 'item' : 'items'})`}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {query.trim() ? `No files or folders match "${query.trim()}" in ${currentName}.` : 'This folder is empty.'}
            </Typography>
          ) : (
            <List disablePadding>
              {items.map((item) => (
                <ListItemButton
                  key={`${item.type}-${item.id}`}
                  onClick={() => openItem(item)}
                  onContextMenu={(event) => {
                    event.preventDefault();
                    setMenu({ mouseX: event.clientX, mouseY: event.clientY, item });
                  }}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemIcon>
                    {item.type === 'folder' ? <FolderIcon color="primary" /> : <TypeIcon fileType={item.fileType} />}
                  </ListItemIcon>
                  <ListItemText primary={item.displayName} secondary={item.secondary} />
                </ListItemButton>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Menu
        open={Boolean(menu)}
        onClose={() => setMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={menu ? { top: menu.mouseY, left: menu.mouseX } : undefined}
      >
        {menuItem?.type === 'folder' ? (
          [
            <MenuItem key="open" onClick={() => { openFolder(menuItem.id); setMenu(null); }}>
              <FolderOpenIcon sx={{ mr: 1 }} fontSize="small" />
              Open
            </MenuItem>,
            <MenuItem
              key="rename"
              onClick={() => {
                const folder = library.folders.find((item) => item.id === menuItem.id);
                setFolderDialog({
                  mode: 'rename',
                  id: menuItem.id,
                  name: folder?.name || '',
                  description: folder?.description || '',
                  error: '',
                });
                setMenu(null);
              }}
            >
              <EditIcon sx={{ mr: 1 }} fontSize="small" />
              Rename
            </MenuItem>,
            <MenuItem key="delete" onClick={() => { setPendingDelete(menuItem); setMenu(null); }}>
              <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
              Delete
            </MenuItem>,
          ]
        ) : menuItem ? (
          [
            <MenuItem key="details" onClick={() => { setDetailsId(menuItem.id); setMenu(null); }}>
              <ViewIcon sx={{ mr: 1 }} fontSize="small" />
              View Details
            </MenuItem>,
            <MenuItem
              key="download"
              onClick={() => {
                const file = library.files.find((item) => item.id === menuItem.id);
                if (file) downloadFile(file);
                setMenu(null);
              }}
            >
              <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
              Download
            </MenuItem>,
            <MenuItem
              key="rename-tag"
              onClick={() => {
                setRenameTarget({ id: menuItem.id, tag: menuItem.displayName, error: '' });
                setMenu(null);
              }}
            >
              <EditIcon sx={{ mr: 1 }} fontSize="small" />
              Rename File Tag
            </MenuItem>,
            <MenuItem key="delete" onClick={() => { setPendingDelete(menuItem); setMenu(null); }}>
              <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
              Delete
            </MenuItem>,
          ]
        ) : null}
      </Menu>

      <Dialog open={Boolean(folderDialog)} onClose={() => setFolderDialog(null)} fullWidth maxWidth="sm">
        <DialogTitle>{folderDialog?.mode === 'rename' ? 'Rename Folder' : 'Create New Folder'}</DialogTitle>
        <DialogContent>
          {folderDialog?.error ? (
            <Typography color="error" variant="body2" sx={{ mb: 2 }} role="alert">
              {folderDialog.error}
            </Typography>
          ) : null}
          <TextField
            autoFocus
            margin="dense"
            label="Folder Name"
            fullWidth
            required
            value={folderDialog?.name || ''}
            onChange={(event) => setFolderDialog((prev) => ({ ...prev, name: event.target.value, error: '' }))}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            minRows={3}
            placeholder="Optional folder description"
            value={folderDialog?.description || ''}
            onChange={(event) => setFolderDialog((prev) => ({ ...prev, description: event.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setFolderDialog(null)}>Cancel</Button>
          <Button type="button" variant="contained" onClick={submitFolder}>
            {folderDialog?.mode === 'rename' ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(renameTarget)} onClose={() => setRenameTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Rename File Tag</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="New File Tag"
            fullWidth
            required
            value={renameTarget?.tag || ''}
            onChange={(event) => setRenameTarget((prev) => ({ ...prev, tag: event.target.value, error: '' }))}
            helperText="This tag must be unique among your files."
            error={Boolean(renameTarget?.error)}
          />
          {renameTarget?.error ? (
            <Typography color="error" variant="body2" sx={{ mt: 1 }} role="alert">
              {renameTarget.error}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button type="button" onClick={() => setRenameTarget(null)}>Cancel</Button>
          <Button type="button" variant="contained" startIcon={<EditIcon />} onClick={submitRename}>
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(details)} onClose={() => setDetailsId('')} fullWidth maxWidth="sm">
        <DialogTitle>{details?.fileTag || 'File'}</DialogTitle>
        <DialogContent>
          {details ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Original name: {details.originalName}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Type: {FILE_TYPE_LABELS[details.fileType] || details.fileType}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Size: {formatFileSize(details.fileSize)}</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Folder: {folderPath(library.folders, details.folderId)}
              </Typography>
              {details.fileType === FILE_TYPES.IMAGE && detailsUrl ? (
                <Box
                  component="img"
                  src={detailsUrl}
                  alt={details.originalName}
                  sx={{ display: 'block', maxWidth: '100%', maxHeight: 280, mb: 2, borderRadius: 1 }}
                />
              ) : null}
              {details.fileType === FILE_TYPES.IMAGE && !detailsUrl ? (
                <Typography variant="body2" color="text.secondary">Loading preview…</Typography>
              ) : null}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button type="button" component="label" startIcon={<UploadIcon />}>
            Replace file
            <input
              hidden
              type="file"
              onChange={(event) => {
                const file = event.target.files && event.target.files[0];
                event.target.value = '';
                if (file) replaceDetailsFile(file);
              }}
            />
          </Button>
          <Button
            type="button"
            startIcon={<DownloadIcon />}
            disabled={!details}
            onClick={() => details && downloadFile(details)}
          >
            Download
          </Button>
          <Button type="button" onClick={() => setDetailsId('')}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={uploadOpen} onClose={closeUpload} fullWidth maxWidth="lg">
        <DialogTitle>
          {uploadStep === 'select' ? 'Upload Files' : `Review ${uploadItems.length} File${uploadItems.length === 1 ? '' : 's'}`}
        </DialogTitle>
        <DialogContent>
          {uploadError ? (
            <Typography color="error" variant="body2" sx={{ mb: 2 }} role="alert">
              {uploadError}
            </Typography>
          ) : null}
          {uploadStep === 'select' ? (
            <>
              <Box
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragOver(false);
                  addUploadFiles(event.dataTransfer.files);
                }}
                onClick={() => document.getElementById('my-files-input')?.click()}
                sx={{
                  border: '2px dashed',
                  borderColor: dragOver ? 'primary.main' : 'grey.300',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  bgcolor: dragOver ? 'action.hover' : 'background.paper',
                  mb: 2,
                }}
              >
                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" gutterBottom>Drop files here or click to browse</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select one or more files from your computer
                </Typography>
              </Box>
              <input
                id="my-files-input"
                type="file"
                multiple
                hidden
                onChange={(event) => {
                  addUploadFiles(event.target.files);
                  event.target.value = '';
                }}
              />
              {uploadItems.length > 0 ? (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {uploadItems.length} file{uploadItems.length === 1 ? '' : 's'} selected
                  </Typography>
                  <Button type="button" variant="outlined" onClick={() => setUploadStep('review')}>
                    Continue to Review
                  </Button>
                </Box>
              ) : null}
            </>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Original Name</TableCell>
                      <TableCell>File Tag</TableCell>
                      <TableCell>File Type</TableCell>
                      <TableCell align="center" sx={{ width: 96 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {uploadItems.map((item) => (
                      <TableRow key={item.key}>
                        <TableCell>
                          <Typography variant="body2" noWrap title={item.originalName}>{item.originalName}</Typography>
                          <Typography variant="caption" color="text.secondary">{formatFileSize(item.fileSize)}</Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={item.fileTag}
                            placeholder="Enter a unique file tag"
                            onChange={(event) => {
                              const fileTag = event.target.value;
                              setUploadItems((prev) => prev.map((row) => (
                                row.key === item.key ? { ...row, fileTag } : row
                              )));
                              setUploadError('');
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <FormControl fullWidth size="small" sx={{ mb: item.fileType === FILE_TYPES.CUSTOM_VPF ? 1 : 0 }}>
                            <InputLabel id={`type-${item.key}`}>Type</InputLabel>
                            <Select
                              labelId={`type-${item.key}`}
                              label="Type"
                              value={item.fileType}
                              onChange={(event) => {
                                const fileType = event.target.value;
                                setUploadItems((prev) => prev.map((row) => (
                                  row.key === item.key ? { ...row, fileType } : row
                                )));
                                setUploadError('');
                              }}
                            >
                              {FILE_TYPE_VALUES.map((type) => (
                                <MenuItem key={type} value={type}>{FILE_TYPE_LABELS[type]}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {item.fileType === FILE_TYPES.CUSTOM_VPF ? (
                            <TextField
                              size="small"
                              fullWidth
                              label="Source Path"
                              placeholder="Relative path on client"
                              value={item.customVpfSourcePath}
                              onChange={(event) => {
                                const customVpfSourcePath = event.target.value;
                                setUploadItems((prev) => prev.map((row) => (
                                  row.key === item.key ? { ...row, customVpfSourcePath } : row
                                )));
                              }}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="caption" color="text.secondary">Ready</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" color="text.secondary">
                File tags must be unique among your files. Defaults use the file name without its extension.
                Photos must be PNG, JPEG, or JPG. Videos must be MP4. Each file must be 15 MB or smaller.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          {uploadStep === 'review' ? (
            <Button type="button" onClick={() => setUploadStep('select')}>Back</Button>
          ) : null}
          <Button type="button" onClick={closeUpload}>Cancel</Button>
          {uploadStep === 'review' ? (
            <Button
              type="button"
              variant="contained"
              startIcon={<UploadIcon />}
              disabled={uploadItems.length === 0}
              onClick={submitUpload}
            >
              {`Upload ${uploadItems.length} File${uploadItems.length === 1 ? '' : 's'}`}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <NotificationDialog
        open={Boolean(notice)}
        onClose={() => setNotice(null)}
        title={notice?.title || ''}
        message={notice?.message || ''}
        iconPreset="warning"
        buttons={['dismiss']}
        buttonLabels={{ dismiss: 'OK' }}
      />
      <NotificationDialog
        open={Boolean(pendingDelete)}
        onClose={() => setPendingDelete(null)}
        title={pendingDelete?.type === 'folder' ? 'Delete folder?' : 'Delete file?'}
        message={pendingDelete?.type === 'folder'
          ? `Delete ${pendingDelete.displayName} and everything inside it? Imaging rows that used these files will drop that file link.`
          : `Delete ${pendingDelete?.displayName || 'this file'}? Imaging rows that used this file will drop that file link.`}
        iconPreset="warning"
        buttons={['reject', 'accept']}
        buttonLabels={{ reject: 'Cancel', accept: 'Delete' }}
        onAction={{ accept: confirmDelete }}
      />
    </PageLayout>
  );
};

const MyFiles = () => (
  <Routes>
    <Route index element={<MyFilesBrowser />} />
    <Route path=":folderId" element={<MyFilesBrowser />} />
    <Route path="*" element={<Navigate to={myFilesHomePath()} replace />} />
  </Routes>
);

export default MyFiles;
